import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";
import { useState } from "react";

import { type MfaPolicy, User, USER_ROLE } from "../types/entities";

export interface UsersTableProps {
  users: User[];
  currentUserEmail?: string;
  isCurrentUserAdmin: boolean;
  isCurrentUserOwner: boolean;
  isSwitchingClient: boolean;
  mfaPolicy?: MfaPolicy;
  onDeleteUser: (userId: string) => void;
  onChangeRole: (userId: string) => void;
  onTransferOwnership: (userId: string) => void;
  language: SupportedLanguage;
}

export const UsersTable = ({
  users,
  currentUserEmail,
  isCurrentUserAdmin,
  isCurrentUserOwner,
  isSwitchingClient,
  mfaPolicy,
  onDeleteUser,
  onChangeRole,
  onTransferOwnership,
  language,
}: UsersTableProps) => {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<{
    [key: string]: HTMLElement | null;
  }>({});
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState<string | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    userId: string
  ) => {
    setUserMenuAnchorEl((prev) => ({ ...prev, [userId]: event.currentTarget }));
  };

  const handleCloseMenu = (userId: string) => {
    setUserMenuAnchorEl((prev) => ({ ...prev, [userId]: null }));
  };

  const handleRequestDeleteUser = (userId: string) => {
    setPendingDeleteUserId(userId);
    setIsDeleteDialogOpen(true);
    handleCloseMenu(userId);
  };

  const handleCancelDeleteUser = () => {
    setIsDeleteDialogOpen(false);
    setPendingDeleteUserId(null);
  };

  const handleConfirmDeleteUser = () => {
    if (pendingDeleteUserId) {
      onDeleteUser(pendingDeleteUserId);
    }
    setIsDeleteDialogOpen(false);
    setPendingDeleteUserId(null);
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, color: "#333", width: "50%" }}>
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.users.table.columns.name"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#333", width: "30%" }}>
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.users.table.columns.role"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#333", width: "50%" }}>
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.users.table.columns.status"
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "#333",
                  width: "20%",
                  textAlign: "left",
                }}
              >
                {getLocalizedMessage(
                  language,
                  "homePortalPage.tabs.users.table.columns.actions"
                )}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {mfaPolicy === "totp-required" && (
              <TableRow>
                <TableCell colSpan={4} sx={{ py: 1 }}>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.users.table.mfaHint"
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {isSwitchingClient ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 8 }}>
                  <CircularProgress size={40} sx={{ color: "#3ec6ff" }} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    {getLocalizedMessage(
                      language,
                      "homePortalPage.tabs.users.table.noUsers"
                    )}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isCurrentUser =
                  currentUserEmail && user.email === currentUserEmail;
                const isTargetUserOwner = user.role === USER_ROLE.OWNER;
                const isTargetUserAdmin = user.role === USER_ROLE.ADMIN;
                const canManageUsers = isCurrentUserAdmin || isCurrentUserOwner;
                const canDelete =
                  canManageUsers &&
                  !isCurrentUser &&
                  !isTargetUserOwner &&
                  (!isTargetUserAdmin || isCurrentUserOwner);
                const canChangeRole = isCurrentUserOwner && !isTargetUserOwner;
                const canTransferOwnership =
                  isCurrentUserOwner && !isTargetUserOwner && !isCurrentUser;

                return (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, color: "#333", mb: 0.5 }}
                      >
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", fontSize: "0.875rem" }}
                      >
                        {user.email}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ color: "#666" }}>{user.role}</TableCell>

                    <TableCell sx={{ color: "#666" }}>
                      {user.firstName ? (
                        <Chip
                          label={getLocalizedMessage(
                            language,
                            "homePortalPage.tabs.users.table.status.active"
                          )}
                          sx={{
                            color: "#008336",
                            fontWeight: 500,
                            backgroundColor: "#e6f9e6",
                            mr: 1,
                          }}
                        />
                      ) : (
                        <Chip
                          label={getLocalizedMessage(
                            language,
                            "homePortalPage.tabs.users.table.status.invitationPending"
                          )}
                          sx={{
                            color: "#313849",
                            fontWeight: 500,
                            backgroundColor: "#EBE3F4",
                            mr: 1,
                            minWidth: "max-content",
                          }}
                        />
                      )}

                      {user.mfaStatus &&
                        (() => {
                          const chipByStatus: Record<
                            | "not-required"
                            | "pending-invite"
                            | "required-not-configured"
                            | "configured",
                            { labelKey: string; sx: Record<string, string> }
                          > = {
                            "not-required": {
                              labelKey:
                                "homePortalPage.tabs.users.table.mfa.notRequired",
                              sx: { color: "#666", backgroundColor: "#f5f5f5" },
                            },
                            "pending-invite": {
                              labelKey:
                                "homePortalPage.tabs.users.table.mfa.pendingInvite",
                              sx: {
                                color: "#6a3e96",
                                backgroundColor: "#F3E9FB",
                              },
                            },
                            "required-not-configured": {
                              labelKey:
                                "homePortalPage.tabs.users.table.mfa.requiredNotConfigured",
                              sx: {
                                color: "#b71c1c",
                                backgroundColor: "#FFEBEE",
                              },
                            },
                            configured: {
                              labelKey:
                                "homePortalPage.tabs.users.table.mfa.configured",
                              sx: {
                                color: "#0052cc",
                                backgroundColor: "#E8F0FF",
                              },
                            },
                          };

                          const def = chipByStatus[user.mfaStatus];
                          return (
                            <Chip
                              label={getLocalizedMessage(
                                language,
                                def.labelKey
                              )}
                              sx={{ fontWeight: 500, ...def.sx }}
                            />
                          );
                        })()}
                    </TableCell>

                    <TableCell sx={{ color: "#666", textAlign: "left" }}>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMenu(e, user.id)}
                        sx={{ color: "#666" }}
                      >
                        <MoreVertIcon />
                      </IconButton>

                      <Menu
                        anchorEl={userMenuAnchorEl[user.id]}
                        open={Boolean(userMenuAnchorEl[user.id])}
                        onClose={() => handleCloseMenu(user.id)}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        {canDelete && (
                          <MenuItem
                            onClick={() => handleRequestDeleteUser(user.id)}
                            sx={{
                              color: "#d32f2f",
                              "&:hover": { backgroundColor: "#ffebee" },
                            }}
                          >
                            <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
                            {getLocalizedMessage(
                              language,
                              "homePortalPage.tabs.users.table.actions.delete"
                            )}
                          </MenuItem>
                        )}

                        {canChangeRole && (
                          <MenuItem
                            onClick={() => {
                              onChangeRole(user.id);
                              handleCloseMenu(user.id);
                            }}
                            sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                          >
                            <EditIcon sx={{ mr: 1, fontSize: 18 }} />
                            {getLocalizedMessage(
                              language,
                              "homePortalPage.tabs.users.table.actions.changeRole"
                            )}
                          </MenuItem>
                        )}

                        {canTransferOwnership && (
                          <MenuItem
                            onClick={() => {
                              onTransferOwnership(user.id);
                              handleCloseMenu(user.id);
                            }}
                            sx={{ "&:hover": { backgroundColor: "#f5f5f5" } }}
                          >
                            <SwapHorizIcon sx={{ mr: 1, fontSize: 18 }} />
                            {getLocalizedMessage(
                              language,
                              "homePortalPage.tabs.users.table.actions.transferOwnership"
                            )}
                          </MenuItem>
                        )}

                        {!canDelete &&
                          !canChangeRole &&
                          !canTransferOwnership && (
                            <MenuItem disabled>
                              <Typography
                                variant="body2"
                                sx={{ color: "#999" }}
                              >
                                {isCurrentUser
                                  ? getLocalizedMessage(
                                      language,
                                      "homePortalPage.tabs.users.table.actions.cannotModifyOwnAccount"
                                    )
                                  : isTargetUserOwner
                                    ? getLocalizedMessage(
                                        language,
                                        "homePortalPage.tabs.users.table.actions.cannotModifyOwnerAccount"
                                      )
                                    : isTargetUserAdmin && !isCurrentUserOwner
                                      ? getLocalizedMessage(
                                          language,
                                          "homePortalPage.tabs.users.table.actions.onlyOwnerCanModifyAdmins"
                                        )
                                      : !canManageUsers
                                        ? getLocalizedMessage(
                                            language,
                                            "homePortalPage.tabs.users.table.actions.onlyAdminsCanModifyUsers"
                                          )
                                        : getLocalizedMessage(
                                            language,
                                            "homePortalPage.tabs.users.table.actions.cannotModifyUser"
                                          )}
                              </Typography>
                            </MenuItem>
                          )}
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDeleteUser}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {getLocalizedMessage(
            language,
            "homePortalPage.tabs.users.deleteDialog.title"
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.users.deleteDialog.message1"
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getLocalizedMessage(
              language,
              "homePortalPage.tabs.users.deleteDialog.message2"
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCancelDeleteUser}>
            {getLocalizedMessage(language, "dialogActions.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDeleteUser}
            color="error"
            variant="contained"
          >
            {getLocalizedMessage(language, "dialogActions.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
